import {
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Typography,
} from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useAppState } from "@/modules/app/useAppContext";
import { RelativeComponentLoader } from "@/components/loader";
import type { GetOpportunitiesDataResponse, Page } from "../types";
import { OpportunitiesSection } from "./opportunities";
import { GroupsTable } from "./groups";
import { PrioritySection } from "./priority";
import styles from "../styles.module.scss";

interface ExplorerTabsProps {
  data: Page<GetOpportunitiesDataResponse> | undefined;
  // Loading state for the All-tab data. Rendered inside the tab panel so
  // the surrounding chrome (header bar, tab strip) stays mounted instead
  // of being replaced by a full-page loader (which caused the bar to
  // flicker on tab switches).
  isLoading?: boolean;
}

export const ExplorerTabs = ({ data, isLoading = false }: ExplorerTabsProps) => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { enablePriorityTab } = useAppState();
  const priorityTabEnabled = enablePriorityTab ?? false;
  // When the Priority tab is enabled for the tenant it becomes the
  // default landing tab on /discover; otherwise fall back to Opportunities.
  const defaultTab = priorityTabEnabled ? "priority" : "opportunities";
  const rawTab = searchParams.get("tab") || defaultTab;
  const tab = rawTab === "priority" && !priorityTabEnabled ? "opportunities" : rawTab;

  return (
    <Card>
      <CardContent className={"!al-border-none al-pt-6"}>
        <Typography variant="section-heading" className="al-mb-4">
          Opportunities
        </Typography>
        <Tabs value={tab} onValueChange={value => updateSearchParamsByKey({ tab: value })}>
          <TabsList>
            {priorityTabEnabled && <TabsTrigger value="priority">Prioritized</TabsTrigger>}
            <TabsTrigger value="opportunities">All</TabsTrigger>
            <TabsTrigger value="groups">Groups</TabsTrigger>
          </TabsList>
          {priorityTabEnabled && (
            <TabsContent value="priority">
              <PrioritySection />
            </TabsContent>
          )}
          <TabsContent value="opportunities">
            {isLoading ? (
              <RelativeComponentLoader label={"Loading..."} />
            ) : (
              data && <OpportunitiesSection data={data} />
            )}
          </TabsContent>
          <TabsContent value="groups">
            <div className={styles.datagrid}>
              <GroupsTable />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
